import React, { FC, useCallback, useMemo } from 'react';
import styles from '../index.module.scss';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, notification, useMe } from 'src/common';
import { trackCompanyDetailsProvided } from 'src/segment/events';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'src/pages/paths';
import { useInitCompanyInfo } from 'src/models/company/hooks';
import { Ownerships, ownership } from 'src/models/ownership';

interface FormState {
  companyName: string;
  orgNumber: string;
}

export const CompanyInfoPage: FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [updateCompanyInfo] = useInitCompanyInfo();
  const onFinish = useCallback(
    (values: FormState) => {
      const { companyName, orgNumber } = values;
      me?.email &&
        updateCompanyInfo({ variables: { companyName, orgNumber } })
          .then(result => {
            const { data } = result;
            data &&
              trackCompanyDetailsProvided({
                id: data.updateUser.company.id,
                name: companyName
              });

            if (ownership.name !== Ownerships.Clira) {
              navigate(Paths.INVITE_TEAM);
            } else {
              navigate(Paths.HOME_PATH);
            }
          })
          .catch(() => {
            notification.error({
              message: t('Common.requestFailed'),
              description: t('Common.unknownErrorDesc')
            });
          });
    },
    [me?.email, navigate, t, updateCompanyInfo]
  );

  const initialValues = useMemo((): FormState => {
    return {
      companyName: '',
      orgNumber: ''
    };
  }, []);

  return (
    <Form form={form} initialValues={initialValues} onFinish={onFinish} layout={'vertical'} className={styles.form}>
      <div className={styles.heading}>
        <h3 className={styles.title}>
          <span>{t('Auth.company.title')}</span>
        </h3>
      </div>
      <Form.Item
        label={t('Auth.company.labels.company')}
        name="companyName"
        rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Company.companyName') }) }]}
      >
        <Input type="text" size={'large'} data-name="companyName" placeholder={t('Company.companyName')} />
      </Form.Item>
      <Form.Item
        name="orgNumber"
        label={t('Auth.company.labels.orgId')}
        rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Company.orgNumber') }) }]}
      >
        <Input type="text" size={'large'} data-name="orgNumber" placeholder={t('Company.orgNumber')} />
      </Form.Item>
      <div className={styles.actions}>
        <Button htmlType={'submit'} type={'primary'}>
          {t('Auth.common.buttons.continue')}
        </Button>
      </div>
    </Form>
  );
};

export default CompanyInfoPage;
